import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronDown, Pause, PhoneCall, Play, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "../../components/ui/badge";
import { CopyButton } from "../../components/ui/copy-button";
import { getCallbackInstant, formatCallbackWhen, isWithinNextHour } from "../../lib/time";
import { CLASSIFICATION_META, STATUS_META, type Callback } from "./types";

interface CallbackCardProps {
  callback: Callback;
  expanded: boolean;
  selected: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onPauseResume: () => void;
  onReschedule: () => void;
}

export function CallbackCard({
  callback: cb, expanded, selected,
  onToggleExpand, onToggleSelect, onPauseResume, onReschedule,
}: CallbackCardProps) {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  const when = getCallbackInstant(cb.callbackDate, cb.callbackTime, cb.timezone, cb.scheduledForUtc);
  const urgent = Boolean(when && isWithinNextHour(when) && (cb.status === "scheduled" || cb.status === "pending"));
  const paused = cb.status === "paused";
  const terminal = ["completed", "failed", "cancelled"].includes(cb.status);
  const cls = CLASSIFICATION_META[cb.classification];
  const st = STATUS_META[cb.status];

  return (
    <div
      className={cn(
        "rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] transition-all",
        urgent && "border-l-4 border-l-warning bg-warning-soft/30",
        !expanded && "hover:shadow-card-hover",
      )}
    >
      {/* ── Collapsed header ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
        className="flex w-full cursor-pointer items-center gap-4 p-4"
      >
        <input
          type="checkbox"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggleSelect}
          aria-label={`Select ${cb.contactName}`}
          className="h-4 w-4 shrink-0 rounded border-line accent-brand"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-bold text-ink">{cb.contactName}</span>
            <span className="font-mono text-xs text-ink-3">{cb.phone}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 font-mono text-xs text-ink-3">
            <div className="flex items-center gap-1">
              {cb.scheduleId}
              <CopyButton value={cb.scheduleId} />
            </div>
            <span>•</span>
            <span className="flex items-center gap-1" title="Callback At">
              <CalendarClock className="h-3 w-3" />
              {formatCallbackWhen(cb.callbackDate, cb.callbackTime, cb.timezone, globalTz, cb.scheduledForUtc)}
            </span>
          </div>
        </div>

        <Badge tone={cls.tone}>{cls.label}</Badge>
        <Badge tone={st.tone} pulse={cb.status === "scheduled"}>{st.label}</Badge>

        {urgent && (
          <span className="flex items-center gap-1 text-xs font-bold text-warning">
            <CalendarClock className="h-3.5 w-3.5" /> &lt;1h
          </span>
        )}

        {!terminal && (
          <button
            onClick={(e) => { e.stopPropagation(); onPauseResume(); }}
            aria-label={paused ? `Resume callback for ${cb.contactName}` : `Pause callback for ${cb.contactName}`}
            className={cn(
              "rounded-lg p-2 transition-colors",
              paused ? "text-success hover:bg-success-soft" : "text-warning hover:bg-warning-soft",
            )}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        )}

        <ChevronDown className={cn("h-4 w-4 text-ink-3 transition-transform", expanded && "rotate-180")} />
      </div>

      {/* ── Expanded details ── */}
      {expanded && (
        <div className="border-t border-line p-4">
          <div className="rounded-lg bg-soft p-4">
            <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Callback Reason (AI Summary)
            </p>
            <p className="text-sm leading-relaxed text-ink-2">{cb.aiSummary}</p>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
            <Field label="Contact ID" value={cb.contactId} mono />
            <Field label="Callback At" value={formatCallbackWhen(cb.callbackDate, cb.callbackTime, cb.timezone, globalTz, cb.scheduledForUtc)} mono />
            <Field label="Timezone" value={cb.timezone} mono />
            <Field label="Callback Type" value={cb.callbackType} />
            <Field label="Callback Value" value={cb.callbackValue} />
          </dl>

          <div className="mt-4 flex items-center gap-2 border-t border-line pt-4">
            {/* <button
              onClick={onReschedule}
              className="rounded-lg border border-line bg-elevated px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-soft"
            >
              Reschedule
            </button> */}
            <Link
              to={`/call-history?q=${encodeURIComponent(cb.contactId)}`}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-soft"
            >
              <PhoneCall className="h-3.5 w-3.5" /> View History
            </Link>
            <div className="flex-1" />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">{label}</dt>
      <dd className={cn("mt-0.5 text-sm text-ink", mono && "font-mono")}>{value}</dd>
    </div>
  );
}
